import { createSelector } from '@reduxjs/toolkit';

import { TrussGroup } from '@paragon/contract-library';
import { forceArray, groupByWithoutNullishKeys } from '@paragon/utilities';

import { RootState } from '..';
import { getSelectedItemEntries } from './getSelectedItemSelector';
import { getInsertingObjectInfo } from './insertionObjectSelectors';
import { getTrussEnvelopeGuidsByComponentDesignGuid } from './layoutDataSelectors';
import { getPickingObjectInfo } from './pickingSelectors';

export const getExistingTrussGroupNames = createSelector(
  (state: RootState) => state.trussGroups,

  (trussGroups) => Object.values(trussGroups).map((group) => group.name.trim()),
);

export const getTrussEnvelopeToTrussGroups = createSelector(
  (state: RootState) => state.trussGroups,

  (trussGroups): Record<string, readonly string[]> => {
    const trussEnvelopeToTrussGroups: Record<string, string[]> = {};
    for (const trussGroup of Object.values(trussGroups)) {
      for (const trussEnvelopeGuid of trussGroup.trussEnvelopeGuids) {
        if (!trussEnvelopeToTrussGroups[trussEnvelopeGuid]) {
          trussEnvelopeToTrussGroups[trussEnvelopeGuid] = [];
        }
        trussEnvelopeToTrussGroups[trussEnvelopeGuid].push(trussGroup.guid);
      }
    }
    return trussEnvelopeToTrussGroups;
  },
);

const getTrussEnvelopeGuidsForTrussGroupWhilePicking = createSelector(
  getInsertingObjectInfo,
  getPickingObjectInfo,
  (state: RootState) => state.trussGroups,

  (insertingObjectInfo, pickingObjectInfo, trussGroups): readonly string[] | null => {
    if (pickingObjectInfo?.type === 'pickingTrussEnvelopesForTrussGroup') {
      let trussEnvelopeGuids: readonly string[];
      if (pickingObjectInfo.trussGroupGuid !== null) {
        trussEnvelopeGuids = trussGroups[pickingObjectInfo.trussGroupGuid].trussEnvelopeGuids;
      } else {
        if (insertingObjectInfo?.type !== 'trussGroup')
          throw new Error('Expected insertingObjectInfo to be a TrussGroup');
        trussEnvelopeGuids = insertingObjectInfo.data.trussEnvelopeGuids;
      }
      // It is an intentional choice to not get recursive children truss envelopes here, as that would make picking
      // confusing. Just include the truss envelopes that are directly part of the truss group.
      const result = [...trussEnvelopeGuids];
      for (const guid of pickingObjectInfo.trussEnvelopeGuidsToAdd) {
        result.push(guid);
      }
      for (const guid of pickingObjectInfo.trussEnvelopeGuidsToRemove) {
        const index = result.indexOf(guid);
        if (index !== -1) {
          result.splice(index, 1);
        }
      }
      return result;
    }
    return null;
  },
);

export const getTrussEnvelopeGuidsToHighlight = createSelector(
  (state: RootState) => state.selections.hoveredJackComponentGuid,
  (state: RootState) => state.selections.hoveredComponentDesignGuidInList,
  getTrussEnvelopeGuidsByComponentDesignGuid,
  (state: RootState) => state.selections.hoveredTrussGroupGuidInList,
  (state: RootState) => state.trussGroups,
  getTrussEnvelopeGuidsForTrussGroupWhilePicking,
  getInsertingObjectInfo,
  getSelectedItemEntries,

  (
    hoveredJackComponentGuid,
    hoveredComponentDesignGuidInList,
    trussEnvelopesByComponentDesignGuid,
    hoveredTrussGroupGuidInList,
    trussGroups,
    trussEnvelopesForPickingOperation,
    insertingObjectInfo,
    selectedItemEntries,
  ): readonly string[] => {
    if (hoveredJackComponentGuid) {
      return trussEnvelopesByComponentDesignGuid[hoveredJackComponentGuid] ?? [];
    }

    if (hoveredComponentDesignGuidInList) {
      return trussEnvelopesByComponentDesignGuid[hoveredComponentDesignGuidInList] ?? [];
    }

    if (hoveredTrussGroupGuidInList) {
      return getAllTrussEnvelopeGuidsRecursive([hoveredTrussGroupGuidInList], trussGroups);
    }

    if (trussEnvelopesForPickingOperation) {
      return trussEnvelopesForPickingOperation;
    }

    if (insertingObjectInfo?.type === 'trussGroup') {
      return insertingObjectInfo.data.trussEnvelopeGuids;
    }

    return getAllTrussEnvelopeGuidsRecursive(
      selectedItemEntries.filter(({ type }) => type === 'trussGroup').flatMap(({ item }) => item.guid),
      trussGroups,
    );
  },
);

export function getAllTrussEnvelopeGuidsRecursive(
  trussGroupGuids: string | readonly string[],
  allTrussGroups: Record<string, TrussGroup>,
): string[] {
  const trussGroupGuidsArray = forceArray(trussGroupGuids);
  const trussEnvelopeGuids = new Set<string>(
    trussGroupGuidsArray.flatMap((trussGroupGuid) => allTrussGroups[trussGroupGuid].trussEnvelopeGuids),
  );

  const allTrussGroupsKeyedByParentGuid = groupByWithoutNullishKeys(
    Object.values(allTrussGroups),
    (trussGroup) => trussGroup.parentGuid,
  );

  for (const trussGroupGuid of trussGroupGuidsArray) {
    let childTrussGroups = allTrussGroupsKeyedByParentGuid[trussGroupGuid] ?? [];
    while (childTrussGroups.length > 0) {
      for (const childTrussGroup of childTrussGroups) {
        for (const childTrussGroupTrussEnvelopeGuid of childTrussGroup.trussEnvelopeGuids) {
          trussEnvelopeGuids.add(childTrussGroupTrussEnvelopeGuid);
        }
      }
      childTrussGroups = childTrussGroups.flatMap(
        (childTrussGroup) => allTrussGroupsKeyedByParentGuid[childTrussGroup.guid] ?? [],
      );
    }
  }

  return Array.from(trussEnvelopeGuids);
}
